import { inject, Injectable, NgZone } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DialogAnimation, DialogCloseResult, DialogRef, DialogService } from '@progress/kendo-angular-dialog';
import { NotificationService } from '@progress/kendo-angular-notification';
import { format, parseISO } from 'date-fns';
import { arSA, enUS } from 'date-fns/locale';
import { firstValueFrom, map, Observable } from 'rxjs';
import { TranslationKeys } from 'src/app/contracts';
import { APP_DATE_FORMAT, APP_TIME_FORMAT, CoreErrorCodes, IAppSettings, loading$, settings$ } from 'src/app/core/contracts';
import { isArabic } from 'src/app/core/helpers/utils';

@Injectable()
export class UI {
  translate = inject(TranslateService);
  private dialog = inject(DialogService);
  private notification = inject(NotificationService);
  private zone = inject(NgZone);

  private settings?: IAppSettings;
  private dialogAnimation: DialogAnimation = {
    duration: 200,
    type: 'zoom',
    direction: 'up',
  };

  constructor() {
    settings$.subscribe(settings => (this.settings = settings));
  }
  public formatDate(date: Date | string, dateFormat: string = this.settings?.dateFormat || APP_DATE_FORMAT) {
    const locale = isArabic(this.settings?.language) ? arSA : enUS;
    if (!date) {
      return undefined;
    }
    if (typeof date === 'string' || date instanceof String) {
      date = parseISO(date as string);
    }
    return format(date, dateFormat, { locale });
  }
  public formatTime(date: Date | string, timeFormat: string = this.settings?.timeFormat || APP_TIME_FORMAT) {
    if (typeof date === 'string' || date instanceof String) {
      date = parseISO(date as string);
    }
    return date ? format(date, timeFormat) : null;
  }
  public get onLanguageChanged$() {
    return this.translate.onLangChange;
  }
  public async showMessage(messageKey?: string, titleKey?: string, interpolateParams?: unknown, buttonText?: string) {
    return new Promise<DialogRef>(resolve => {
      // NOTE: quirky workaround for dialog not opening unless a zone trigger action occurs (e.g: click/focus/...), should investigate and fix
      this.zone.run(() => {
        const dialog: DialogRef = this.dialog.open({
          animation: this.dialogAnimation,
          title: this.translate.instant(titleKey) || ' ', // keep the ' ' otherwise the close button/header won't render
          content: this.translate.instant(messageKey),
          actions: [{ text: this.translate.instant(buttonText || TranslationKeys.Common.Close, interpolateParams), themeColor: 'primary' }],
          minHeight: 200,
          minWidth: 300,
          cssClass: 'message-dialog',
        });
        resolve(dialog);
      });
    });
  }
  public async showError(messageKey?: string, titleKey?: string, interpolateParams?: unknown) {
    return new Promise<DialogRef>(resolve => {
      // NOTE: quirky workaround for dialog not opening unless a zone trigger action occurs (e.g: click/focus/...), should investigate and fix
      this.zone.run(() => {
        const dialog: DialogRef = this.dialog.open({
          content: this.translate.instant(messageKey || TranslationKeys.Errors[CoreErrorCodes.Unknown as keyof typeof TranslationKeys.Errors]),
          animation: this.dialogAnimation,
          title: this.translate.instant(titleKey) || ' ', // keep the ' ' otherwise the close button/header won't render
          actions: [{ text: this.translate.instant(TranslationKeys.Common.Dismiss, interpolateParams), cssClass: 'error' }],
          minHeight: 200,
          minWidth: 300,
          cssClass: 'error-dialog',
        });
        resolve(dialog);
      });
    });
  }
  public showConfirm$(messageKey: string, titleKey?: string, interpolateParams?: unknown, cancelText?: string, confirmText?: string): Observable<boolean> {
    const dialog: DialogRef = this.dialog.open({
      animation: this.dialogAnimation,
      title: this.translate.instant(titleKey) || ' ', // keep the ' ' otherwise the close button/header won't render
      content: this.translate.instant(messageKey),
      actions: [
        { text: this.translate.instant(confirmText || TranslationKeys.Common.Yes, interpolateParams), themeColor: 'primary', agree: true },
        { text: this.translate.instant(TranslationKeys.Common.No, interpolateParams), cssClass: 'cancel', agree: false },
      ],
      minHeight: 200,
      minWidth: 300,
      cssClass: 'confirm-dialog',
    });
    return dialog.result.pipe(
      map(result => {
        if (result instanceof DialogCloseResult) {
          return false;
        }
        return result.themeColor === 'primary';
      })
    );
  }
  public showConfirm(messageKey: string, titleKey?: string, interpolateParams?: unknown, cancelText?: string, confirmText?: string): Promise<boolean> {
    const dialog: DialogRef = this.dialog.open({
      animation: this.dialogAnimation,
      title: this.translate.instant(titleKey) || ' ', // keep the ' ' otherwise the close button/header won't render
      content: this.translate.instant(messageKey),
      actions: [
        { text: this.translate.instant(confirmText || TranslationKeys.Common.Yes, interpolateParams), themeColor: 'primary', agree: true },
        { text: this.translate.instant(TranslationKeys.Common.No, interpolateParams), cssClass: 'cancel', agree: false },
      ],
      minHeight: 200,
      minWidth: 300,
      cssClass: 'confirm-dialog',
    });
    return firstValueFrom(
      dialog.result.pipe(
        map(result => {
          if (result instanceof DialogCloseResult) {
            return false;
          }
          return result.themeColor === 'primary';
        })
      )
    );
  }
  public async showLoading(content?: string, interpolateParams?: unknown, showBackdrop: boolean = true) {
    return Promise.resolve(loading$.next(true));
  }
  public hideLoading() {
    return Promise.resolve(loading$.next(false));
  }
  public async showToast(
    messageKey: string,
    interpolateParams?: unknown,
    duration: number = 3000,
    position: 'bottom' | 'top' | 'middle' = 'bottom',
    type: 'none' | 'success' | 'warning' | 'error' | 'info' = 'success'
  ) {
    const onHide = this.notification.show({
      content: this.translate.instant(messageKey, interpolateParams),
      animation: { type: 'slide', duration: 400 },
      hideAfter: duration,
      position: { horizontal: 'center', vertical: position as 'top' | 'bottom' },
      type: { style: type },
      closable: false, // setting to true won't let the toast auto hide after duration
      height: 40,
    }).afterHide as Observable<unknown>;
    if (onHide) {
      return firstValueFrom(onHide);
    } else {
      return Promise.resolve();
    }
  }
}
